import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Project, NotificationProvider, NotificationRule, TableColumn } from '../../types/domain';
import { apiClient } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Checkbox } from '../ui/Checkbox';
import { Textarea } from '../ui/Textarea';
import {
  Mail,
  Sparkles,
  Plus,
  Eye,
  Code,
  AlertTriangle,
  Zap,
  Send,
  Save,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';
import {
  generateDefaultEmailBody,
  getDefaultSubjectTemplate,
  getDefaultRecipients,
  buildSampleDataObject,
} from '../../utils/schemaSnippets';

interface CreateRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (rule: NotificationRule) => void;
  table: Table;
  project: Project;
  providers: NotificationProvider[];
  columns?: TableColumn[];
  initialRule?: NotificationRule | null;
  onOpenSmtpModal?: () => void;
  onOpenResendModal?: () => void;
}

const DEFAULT_EMAIL_BODY = `<!DOCTYPE html>
<html>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; color: #111;">
  <h2 style="color: #111827; font-size: 18px; font-weight: 700; margin-bottom: 12px;">New Submission Captured</h2>
  <p>A new verified form payload was received on Ericius:</p>
  
  {{ form._all_fields }}

  <hr style="border: none; border-top: 1px solid #eaeaea; margin: 24px 0;" />
  <p style="font-size: 12px; color: #6b7280;">
    Submission Ingested At: {{ meta.created_at }} | Origin IP: {{ meta.ip }}
  </p>
</body>
</html>`;

export const CreateRuleModal: React.FC<CreateRuleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  table,
  project,
  providers,
  columns,
  initialRule,
}) => {
  const { showToast } = useToast();
  const { currentOrg } = useAuth();
  const isEditing = Boolean(initialRule);

  const [name, setName] = useState('Ingestion Notification Alert');
  const [providerId, setProviderId] = useState(providers[0]?.id || '');
  const [eventTrigger, setEventTrigger] = useState('non_spam_only');
  const [recipients, setRecipients] = useState(() => getDefaultRecipients(columns));
  const [subjectTemplate, setSubjectTemplate] = useState(() => getDefaultSubjectTemplate(table.name, columns));
  const [bodyTemplate, setBodyTemplate] = useState(() => generateDefaultEmailBody(table.name, columns));
  const [isActive, setIsActive] = useState(true);
  const [previewMode, setPreviewMode] = useState<'editor' | 'preview'>('editor');
  const [isSaving, setIsSaving] = useState(false);

  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Initialize or reset form when modal opens or initialRule changes
  useEffect(() => {
    if (isOpen) {
      if (initialRule) {
        setName(initialRule.name || '');
        setProviderId(initialRule.provider_id || (providers[0]?.id ?? ''));
        setEventTrigger(initialRule.event_trigger || 'non_spam_only');
        setRecipients(initialRule.recipients ? initialRule.recipients.join(', ') : '');
        setSubjectTemplate(initialRule.subject_template || '');
        setBodyTemplate(initialRule.body_template || DEFAULT_EMAIL_BODY);
        setIsActive(initialRule.is_active ?? true);
      } else {
        setName('Ingestion Notification Alert');
        setProviderId(providers[0]?.id || '');
        setEventTrigger('non_spam_only');
        setRecipients(getDefaultRecipients(columns));
        setSubjectTemplate(getDefaultSubjectTemplate(table.name, columns));
        setBodyTemplate(generateDefaultEmailBody(table.name, columns));
        setIsActive(true);
      }
      setPreviewMode('editor');
    }
  }, [isOpen, initialRule, providers, table.name, columns]);

  // Selected provider details
  const selectedProvider = useMemo(() => {
    return providers.find((p) => p.id === providerId) || providers[0];
  }, [providers, providerId]);

  const insertToken = (token: string) => {
    if (!bodyTextareaRef.current) return;
    const start = bodyTextareaRef.current.selectionStart;
    const end = bodyTextareaRef.current.selectionEnd;
    const text = bodyTemplate;
    const before = text.substring(0, start);
    const after = text.substring(end);
    setBodyTemplate(before + token + after);

    setTimeout(() => {
      bodyTextareaRef.current?.focus();
      bodyTextareaRef.current?.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  };

  // Live rendered HTML Preview
  const previewHtml = useMemo(() => {
    let html = bodyTemplate;
    const sample = buildSampleDataObject(columns);

    const tableRows = Object.entries(sample)
      .map(
        ([k, v]) =>
          `    <tr><td style="padding:6px 10px; border:1px solid #e5e7eb; font-family:monospace;">${k}</td><td style="padding:6px 10px; border:1px solid #e5e7eb;">${
            typeof v === 'object' ? JSON.stringify(v) : String(v)
          }</td></tr>`
      )
      .join('\n');

    const allFieldsTable = `
<table style="width:100%; border-collapse:collapse; margin-top:12px; font-size:13px;">
  <thead>
    <tr style="background:#f9fafb; text-align:left;">
      <th style="padding:6px 10px; border:1px solid #e5e7eb;">Field</th>
      <th style="padding:6px 10px; border:1px solid #e5e7eb;">Value</th>
    </tr>
  </thead>
  <tbody>
${tableRows}
  </tbody>
</table>`;

    html = html.replace(/\{\{\s*form\._all_fields\s*\}\}/g, allFieldsTable);
    return html;
  }, [bodyTemplate, columns]);

  const availableTokens = useMemo(() => {
    const list = ['{{ form._all_fields }}', '{{ meta.created_at }}', '{{ meta.ip }}'];
    if (columns && columns.length > 0) {
      columns.forEach((c) => {
        list.push(`{{ form.${c.key} }}`);
      });
    }
    return list;
  }, [columns]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast({ type: 'warning', title: 'Name Required', message: 'Please enter a rule name.' });
      return;
    }

    const recipientList = recipients
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);

    if (recipientList.length === 0) {
      showToast({ type: 'error', title: 'Validation Error', message: 'At least one email recipient is required.' });
      return;
    }

    const payload = {
      table_id: table.id,
      provider_id: providerId || (providers[0]?.id ?? ''),
      name: name.trim(),
      event_trigger: eventTrigger,
      recipients: recipientList,
      subject_template: subjectTemplate.trim() || 'New Form Submission',
      body_template: bodyTemplate.trim() || DEFAULT_EMAIL_BODY,
      is_active: isActive,
    };

    setIsSaving(true);
    try {
      if (isEditing && initialRule) {
        const res = await apiClient<{ rule: NotificationRule }>(
          `/projects/${project.id}/tables/${table.id}/rules/${initialRule.id}`,
          {
            method: 'PUT',
            body: JSON.stringify(payload),
          }
        );

        showToast({
          type: 'success',
          title: 'Notification Rule Updated',
          message: `Rule '${name}' changes saved successfully.`,
        });

        onSuccess(res.rule || { ...initialRule, ...payload });
      } else {
        const res = await apiClient<{ rule: NotificationRule }>(
          `/projects/${project.id}/tables/${table.id}/rules`,
          {
            method: 'POST',
            body: JSON.stringify(payload),
          }
        );

        showToast({
          type: 'success',
          title: 'Notification Rule Created',
          message: `Rule '${name}' is now active for datastore ${table.name}.`,
        });

        onSuccess(res.rule || (res as any));
      }
      onClose();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: isEditing ? 'Failed to update rule' : 'Failed to create rule',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Edit Rule: ${initialRule?.name || 'Notification'}` : `Create Notification Rule for ${table.name}`}
      maxWidth="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        {/* Rule Name & Event Trigger */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            label="Rule Name"
            type="text"
            required
            size="sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sales Team Alert"
          />

          <Select
            label="Trigger Event"
            size="sm"
            value={eventTrigger}
            onChange={(e) => setEventTrigger(e.target.value)}
          >
            <option value="non_spam_only">Verified Submissions Only (Skip Spam)</option>
            <option value="all_submissions">All Submissions (Including Quarantined)</option>
            <option value="spam_only">Spam Quarantined Detections Only</option>
          </Select>
        </div>

        {/* Provider Selector */}
        <div>
          <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
            <span className="block text-cf-textSecondary font-medium text-xs">Notification Provider Connector</span>
            <Link
              to={`/organization/${project.organization_id || currentOrg?.id}/projects/${project.id}/providers`}
              onClick={onClose}
              className="text-cf-orange hover:text-cf-orangeHover hover:underline flex items-center gap-1 font-medium text-xs transition-colors cursor-pointer"
              title="Manage notification providers"
            >
              <span>Manage providers</span>
              <ExternalLink size={11} />
            </Link>
          </div>
          <Select
            size="sm"
            value={providerId}
            onChange={(e) => setProviderId(e.target.value)}
          >
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.provider_type.toUpperCase()}) {p.is_default ? '★ Default' : ''}
              </option>
            ))}
            {providers.length === 0 && (
              <option value="">No providers configured yet</option>
            )}
          </Select>
        </div>

        {/* Recipients for email */}
        <Input
          label="Recipients (Comma-separated email addresses, supports {{ form.email }})"
          type="text"
          required
          size="sm"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          placeholder="alerts@company.com, {{ form.email }}"
        />

        {/* Subject Template */}
        <Input
          label="Email Subject Template"
          type="text"
          required
          size="sm"
          value={subjectTemplate}
          onChange={(e) => setSubjectTemplate(e.target.value)}
          placeholder="New submission: {{ form.name }}"
        />

        {/* Dynamic Variable Chips Row */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-cf-textMuted flex items-center gap-1">
              <Sparkles size={12} className="text-cf-orange" />
              <span>Insert Dynamic Variables:</span>
            </span>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                size="xs"
                variant={previewMode === 'editor' ? 'primary' : 'ghost'}
                onClick={() => setPreviewMode('editor')}
                icon={<Code size={11} />}
              >
                HTML Editor
              </Button>
              <Button
                type="button"
                size="xs"
                variant={previewMode === 'preview' ? 'primary' : 'ghost'}
                onClick={() => setPreviewMode('preview')}
                icon={<Eye size={11} />}
              >
                Live Preview
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            {availableTokens.map((token) => (
              <Button
                key={token}
                type="button"
                size="xs"
                variant="secondary"
                onClick={() => insertToken(token)}
                title="Click to insert at cursor"
              >
                + {token}
              </Button>
            ))}
          </div>
        </div>

        {/* Editor or Preview Pane */}
        {previewMode === 'editor' ? (
          <div>
            <Textarea
              ref={bodyTextareaRef}
              rows={8}
              required
              mono
              value={bodyTemplate}
              onChange={(e) => setBodyTemplate(e.target.value)}
              placeholder="Enter HTML email template..."
            />
          </div>
        ) : (
          <div className="border border-cf-border rounded-lg bg-cf-surface overflow-hidden shadow-inner">
            <div className="px-3 py-1.5 bg-cf-surfaceHover border-b border-cf-border flex items-center justify-between text-xs text-cf-textMuted">
              <span className="">Email Client Sandbox</span>
              <span className="text-xs text-emerald-400">Rendered Preview</span>
            </div>
            <div className="bg-white text-zinc-900 p-4 max-h-56 overflow-y-auto select-text">
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </div>
        )}

        <Checkbox
          label="Rule is active and will evaluate on incoming submissions"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          size="sm"
        />

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-cf-border">
          <Button
            type="button"
            variant="secondary"
            size="xs"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="xs"
            disabled={isSaving || !name.trim()}
            loading={isSaving}
            icon={isEditing ? <Save size={13} /> : <Plus size={13} />}
          >
            {isEditing ? 'Save Changes' : 'Create Rule'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
